/**
 * VideoTrimModal Component
 * Modal for trimming first and/or last frames from a video
 * 
 * Features:
 * - Preview video with trim markers
 * - Adjust frames to remove from start/end
 * - Real-time preview of trim points
 * - Progress indicator during processing
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import videoTrimService, { VideoMetadata } from '../../services/VideoTrimService';

interface VideoTrimModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  onTrimComplete: (trimmedVideoUrl: string) => void;
}

export const VideoTrimModal: React.FC<VideoTrimModalProps> = ({
  open,
  onClose,
  videoUrl,
  onTrimComplete,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trimmedVideoRef = useRef<HTMLVideoElement>(null);
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Trim settings
  const [trimStartFrames, setTrimStartFrames] = useState(0);
  const [trimEndFrames, setTrimEndFrames] = useState(0);
  
  // Preview state - 'editing' = adjusting trim points, 'preview' = viewing trimmed result
  const [mode, setMode] = useState<'editing' | 'preview'>('editing');
  const [trimmedVideoUrl, setTrimmedVideoUrl] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'start' | 'end' | null>(null);

  // Check if FFmpeg is already loaded
  const ffmpegLoaded = videoTrimService.isLoaded();

  // Load video metadata and reset state when opening
  useEffect(() => {
    if (open && videoUrl) {
      setLoading(true);
      setError(null);
      setTrimStartFrames(0);
      setTrimEndFrames(0);
      setMode('editing');
      setTrimmedVideoUrl(null);
      
      videoTrimService.getVideoMetadata(videoUrl)
        .then(setMetadata)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [open, videoUrl]);

  // Clean up trimmed video URL when closing
  useEffect(() => {
    return () => {
      if (trimmedVideoUrl) {
        URL.revokeObjectURL(trimmedVideoUrl);
      }
    };
  }, [trimmedVideoUrl]);

  // Preview start trim point
  const previewStart = useCallback(() => {
    if (videoRef.current && metadata) {
      const startTime = trimStartFrames / metadata.frameRate;
      videoRef.current.currentTime = startTime;
      setPreviewMode('start');
    }
  }, [trimStartFrames, metadata]);

  // Preview end trim point
  const previewEnd = useCallback(() => {
    if (videoRef.current && metadata) {
      const endTime = metadata.duration - (trimEndFrames / metadata.frameRate);
      videoRef.current.currentTime = Math.max(0, endTime - 0.5); // Show half second before end
      setPreviewMode('end');
    }
  }, [trimEndFrames, metadata]);

  // Handle trim - process the video and show preview
  const handleTrim = async () => {
    if (!metadata || (trimStartFrames === 0 && trimEndFrames === 0)) {
      onClose();
      return;
    }

    // Check if trimming is supported
    if (!videoTrimService.isSupported()) {
      setError('Video trimming is not supported in this browser.');
      return;
    }

    setProcessing(true);
    setProgress(0);
    setProgressMessage('Starting...');
    setError(null);

    try {
      const trimmedUrl = await videoTrimService.createTrimmedVideo(
        videoUrl,
        {
          trimStart: trimStartFrames,
          trimEnd: trimEndFrames,
        },
        (prog, message) => {
          setProgress(prog);
          if (message) setProgressMessage(message);
        }
      );
      
      // Show preview instead of immediately completing
      setTrimmedVideoUrl(trimmedUrl);
      setMode('preview');
    } catch (err) {
      console.error('Video trim error:', err);
      setError(err instanceof Error ? err.message : 'Failed to trim video');
    } finally {
      setProcessing(false);
    }
  };

  // Accept the trimmed video - create new output node
  const handleAccept = () => {
    if (trimmedVideoUrl) {
      onTrimComplete(trimmedVideoUrl);
      onClose();
    }
  };

  // Reject and go back to editing
  const handleReject = () => {
    if (trimmedVideoUrl) {
      URL.revokeObjectURL(trimmedVideoUrl);
      setTrimmedVideoUrl(null);
    }
    setMode('editing');
  };

  // Handle close - clean up if needed
  const handleClose = () => {
    if (trimmedVideoUrl) {
      URL.revokeObjectURL(trimmedVideoUrl);
      setTrimmedVideoUrl(null);
    }
    setMode('editing');
    onClose();
  };

  // Calculate new duration after trim
  const getNewDuration = () => {
    if (!metadata) return 0;
    const removedTime = (trimStartFrames + trimEndFrames) / metadata.frameRate;
    return Math.max(0, metadata.duration - removedTime);
  };

  const maxTrimFrames = metadata ? Math.floor(metadata.estimatedFrameCount / 2) - 1 : 10;

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--color-bg-primary)',
          color: 'var(--color-text-primary)',
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCutIcon />
          <Typography variant="h6">
            {mode === 'preview' ? 'Preview Trimmed Video' : 'Trim Video'}
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          disabled={processing}
          sx={{ color: 'var(--color-text-secondary)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error && !processing ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : mode === 'preview' && trimmedVideoUrl ? (
          /* Preview Mode - Show trimmed video result */
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              Video trimmed successfully! Review the result below.
            </Alert>
            
            {/* Trimmed Video Preview */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 3,
            }}>
              <video
                ref={trimmedVideoRef}
                src={trimmedVideoUrl}
                controls
                autoPlay
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  border: '2px solid var(--color-type-video)',
                }}
              />
            </Box>

            {/* Info about what was trimmed */}
            <Box sx={{ 
              backgroundColor: 'var(--color-bg-secondary)', 
              p: 2, 
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              <Typography>
                Trimmed: {trimStartFrames} frame{trimStartFrames !== 1 ? 's' : ''} from start, {trimEndFrames} frame{trimEndFrames !== 1 ? 's' : ''} from end
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                New duration: <strong>{getNewDuration().toFixed(2)}s</strong>
              </Typography>
            </Box>
          </>
        ) : (
          <>
            {/* Video Preview */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 3,
              position: 'relative',
            }}>
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px',
                }}
              />
              {previewMode && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 50,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  Preview: {previewMode === 'start' ? 'New Start' : 'New End'}
                </Box>
              )}
            </Box>

            {/* Video Info */}
            {metadata && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Duration: {metadata.duration.toFixed(2)}s • 
                  {metadata.width}x{metadata.height} • 
                  ~{metadata.estimatedFrameCount} frames @ {metadata.frameRate}fps
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Trim Start Control */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SkipNextIcon sx={{ transform: 'rotate(180deg)', color: 'var(--color-type-video)' }} />
                  <Typography>Remove from start</Typography>
                </Box>
                <Button 
                  size="small" 
                  onClick={previewStart}
                  disabled={trimStartFrames === 0}
                >
                  Preview
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={trimStartFrames}
                  onChange={(_, value) => setTrimStartFrames(value as number)}
                  min={0}
                  max={maxTrimFrames}
                  step={1}
                  disabled={processing}
                  sx={{
                    color: 'var(--color-type-video)',
                    '& .MuiSlider-thumb': {
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: '0 0 0 8px rgba(244, 67, 54, 0.16)',
                      },
                    },
                  }}
                />
                <Typography sx={{ minWidth: '80px', textAlign: 'right' }}>
                  {trimStartFrames} frame{trimStartFrames !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {metadata && trimStartFrames > 0 
                  ? `Removing ${(trimStartFrames / metadata.frameRate).toFixed(2)}s from start (fixes first-frame artifacts)`
                  : 'Slide to remove frames from the beginning'}
              </Typography>
            </Box>

            {/* Trim End Control */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SkipNextIcon sx={{ color: 'var(--color-type-video)' }} />
                  <Typography>Remove from end</Typography>
                </Box>
                <Button 
                  size="small" 
                  onClick={previewEnd}
                  disabled={trimEndFrames === 0}
                >
                  Preview
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={trimEndFrames}
                  onChange={(_, value) => setTrimEndFrames(value as number)}
                  min={0}
                  max={maxTrimFrames}
                  step={1}
                  disabled={processing}
                  sx={{
                    color: 'var(--color-type-video)',
                    '& .MuiSlider-thumb': {
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: '0 0 0 8px rgba(244, 67, 54, 0.16)',
                      },
                    },
                  }}
                />
                <Typography sx={{ minWidth: '80px', textAlign: 'right' }}>
                  {trimEndFrames} frame{trimEndFrames !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {metadata && trimEndFrames > 0 
                  ? `Removing ${(trimEndFrames / metadata.frameRate).toFixed(2)}s from end (fixes Veo3 last-frame issues)`
                  : 'Slide to remove frames from the end'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Summary */}
            <Box sx={{ 
              backgroundColor: 'var(--color-bg-secondary)', 
              p: 2, 
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              {trimStartFrames === 0 && trimEndFrames === 0 ? (
                <Typography color="text.secondary">
                  No frames selected for removal
                </Typography>
              ) : (
                <Typography>
                  New duration: <strong>{getNewDuration().toFixed(2)}s</strong>
                  {metadata && (
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                      {' '}(removing {((trimStartFrames + trimEndFrames) / metadata.frameRate).toFixed(2)}s)
                    </span>
                  )}
                </Typography>
              )}
            </Box>

            {/* Progress */}
            {processing && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Box>
                    <Typography>
                      {progressMessage || 'Processing...'} {Math.round(progress * 100)}%
                    </Typography>
                    {!ffmpegLoaded && progress < 0.2 && (
                      <Typography variant="caption" color="text.secondary">
                        First-time setup: downloading video tools (~25MB)
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: 'var(--color-bg-tertiary)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${progress * 100}%`,
                        backgroundColor: 'var(--color-type-video)',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Browser support warning */}
            {!videoTrimService.isSupported() && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your browser doesn't support video trimming. Please use a modern browser.
              </Alert>
            )}

            {/* First-time setup note */}
            {videoTrimService.isSupported() && !ffmpegLoaded && (trimStartFrames > 0 || trimEndFrames > 0) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                First-time setup will download video tools (~25MB). This is cached for future use.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        borderTop: '1px solid var(--color-border)',
      }}>
        {mode === 'preview' ? (
          /* Preview mode - Accept or Try Again */
          <>
            <Button 
              onClick={handleReject} 
              sx={{ color: 'var(--color-text-secondary)' }}
            >
              Try Again
            </Button>
            <Button
              onClick={handleAccept}
              variant="contained"
              sx={{
                backgroundColor: 'var(--color-success)',
                '&:hover': {
                  backgroundColor: 'var(--color-success)',
                  filter: 'brightness(1.1)',
                },
              }}
            >
              Accept & Create Output
            </Button>
          </>
        ) : (
          /* Editing mode - Cancel or Trim */
          <>
            <Button 
              onClick={handleClose} 
              disabled={processing}
              sx={{ color: 'var(--color-text-secondary)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTrim}
              variant="contained"
              disabled={loading || processing || (trimStartFrames === 0 && trimEndFrames === 0) || !videoTrimService.isSupported()}
              startIcon={processing ? <CircularProgress size={16} /> : <ContentCutIcon />}
              sx={{
                backgroundColor: 'var(--color-type-video)',
                '&:hover': {
                  backgroundColor: 'var(--color-type-video)',
                  filter: 'brightness(1.1)',
                },
              }}
            >
              {processing ? 'Processing...' : 'Trim Video'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VideoTrimModal;
